import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Alert, AutoComplete, Button, DatePicker, Input, InputNumber, Modal, Segmented, Select, Space, Table, Tag } from 'antd';
import { API } from '@/services/api.service';
import { ContractTag } from './PeoplePanel';
import { CALC_KINDS, CURRENCY_OPTIONS, errorText, fold, money } from './format';

const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);
const monthIndex = (p) => p.year * 12 + p.month;

/**
 * The month's calculations in one go. Rent: tick the properties paid this
 * month (those due are ticked already) and get one draft per property with
 * its owners, amounts and accounts. Other income or supplementary work: tick the people, type each
 * one's amount, and get one draft.
 */
export default function BatchCalculationModal({ open, company, projects, payerAccounts, onClose, onCreated }) {
  const { t } = useTranslation('taxReturns');
  const [kind, setKind] = useState('rent');
  const [period, setPeriod] = useState({ month: dayjs().month() + 1, year: dayjs().year() });
  const [rateDate, setRateDate] = useState(dayjs());
  // NBS middle rate per foreign currency in play, e.g. { EUR: 117.2 }.
  const [rates, setRates] = useState({});
  const [currency, setCurrency] = useState('RSD');
  const [payerAccount, setPayerAccount] = useState(null);
  const [picked, setPicked] = useState([]);
  const [amounts, setAmounts] = useState({});
  const [subject, setSubject] = useState('');
  const [project, setProject] = useState(null);
  const [search, setSearch] = useState('');
  const [result, setResult] = useState(null);

  const props = useQuery({
    queryKey: ['tax-properties', company, 'all'],
    queryFn: () => API.taxReturns.getProperties(company).then((d) => d.items),
    enabled: Boolean(open && company),
  });
  const people = useQuery({
    queryKey: ['tax-recipients', company, 'active'],
    queryFn: () => API.taxReturns.getRecipients(company).then((d) => d.items),
    enabled: Boolean(open && company),
  });

  // A property is due when its last calculation's months have run out by the chosen month.
  const rows = useMemo(
    () =>
      (props.data || [])
        .filter((p) => p.active)
        .map((p) => {
          const last = p.lastCalculation;
          const done = last && monthIndex(last.period) === monthIndex(period);
          const due = !last || monthIndex(last.period) + (last.months || p.months) <= monthIndex(period);
          return { ...p, done, due: due && !done };
        }),
    [props.data, period]
  );

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setPicked(kind === 'rent' ? rows.filter((r) => r.due).map((r) => r._id) : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, kind, props.data, period.month, period.year]);

  // The foreign currencies the calculations will need a rate for.
  const rateCurrencies = useMemo(() => {
    const used =
      kind === 'rent' ? rows.filter((r) => picked.includes(r._id)).map((r) => r.currency) : [currency];
    return [...new Set(used)].filter((c) => c && c !== 'RSD').sort();
  }, [kind, rows, picked, currency]);
  const rateKey = rateCurrencies.join(',');

  useEffect(() => {
    if (!open || !rateDate) return;
    const day = rateDate.format('YYYY-MM-DD');
    rateCurrencies.forEach((c) =>
      API.taxReturns
        .getExchangeRate(c, day)
        .then((r) => setRates((s) => ({ ...s, [c]: r.rate || null })))
        .catch(() => setRates((s) => ({ ...s, [c]: null })))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rateDate, rateKey]);

  const create = useMutation({
    mutationFn: () =>
      API.taxReturns.createCalculationsBatch(company, {
        kind,
        period,
        rateDate: rateDate ? rateDate.format('YYYY-MM-DD') : null,
        exchangeRates: Object.fromEntries(rateCurrencies.map((c) => [c, rates[c]])),
        payerAccount: payerAccount || null,
        ...(kind === 'rent'
          ? { properties: picked }
          : {
              subject,
              project,
              currency,
              recipients: picked.map((id) => ({ recipient: id, netPerMonth: amounts[id] })),
            }),
      }),
    onSuccess: (data) => {
      setResult(data);
      onCreated?.();
    },
  });

  const q = fold(search);
  const propertyRows = rows.filter(
    (p) => !q || fold(`${p.address} ${p.project?.code} ${p.owners.map((o) => o.name).join(' ')}`).includes(q)
  );
  const otherPeople = (people.data || []).filter(
    (r) => r.active && r.kinds.includes(kind) && (!q || fold(`${r.name} ${r.idNumber}`).includes(q))
  );
  const missingRate = rateCurrencies.some((c) => !(rates[c] > 0));
  const canCreate =
    picked.length > 0 &&
    !missingRate &&
    (kind === 'rent' || (subject.trim() && picked.every((id) => amounts[id] > 0)));

  const propertyColumns = [
    {
      title: t('people.fields.address'),
      dataIndex: 'address',
      render: (v, p) => (
        <div>
          <div>
            {v} {p.done && <Tag color="green">{t('batch.already')}</Tag>}
            {p.due && <Tag color="blue">{t('batch.due')}</Tag>}
          </div>
          <div className="text-xs opacity-60">
            {p.project ? `${p.project.code} · ` : ''}
            {p.owners.map((o) => o.name).join(', ')}
          </div>
        </div>
      ),
    },
    {
      title: t('people.fields.netPerMonth'),
      dataIndex: 'netPerMonth',
      width: 120,
      align: 'right',
      render: (v, p) => `${money(v)} ${p.currency}`,
    },
    { title: t('people.fields.monthsShort'), dataIndex: 'months', width: 70, align: 'right' },
    {
      title: t('people.fields.lastCalc'),
      dataIndex: 'lastCalculation',
      width: 120,
      render: (c) => (c ? `${c.pid}${c.coveredPeriod ? ` (${c.coveredPeriod})` : ''}` : '—'),
    },
    { title: t('people.fields.contractTo'), dataIndex: 'contractTo', width: 120, render: (v) => <ContractTag to={v} /> },
  ];

  const peopleColumns = [
    { title: t('people.fields.name'), dataIndex: 'name' },
    { title: t('people.fields.idNumber'), dataIndex: 'idNumber', width: 150 },
    {
      title: `${t('batch.amount')} ${currency}`,
      width: 170,
      render: (r) => (
        <InputNumber
          style={{ width: 150 }}
          min={0}
          step={0.01}
          decimalSeparator=","
          disabled={!picked.includes(r._id)}
          value={amounts[r._id]}
          onChange={(v) => setAmounts((a) => ({ ...a, [r._id]: v }))}
        />
      ),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={1120}
      destroyOnClose
      title={t('batch.title')}
      footer={
        result ? (
          <Button shape="round" type="primary" onClick={onClose}>
            {t('batch.close')}
          </Button>
        ) : (
          <Space>
            <Button shape="round" onClick={onClose}>
              {t('people.cancel')}
            </Button>
            <Button shape="round" type="primary" disabled={!canCreate} loading={create.isPending} onClick={() => create.mutate()}>
              {t('batch.create', { count: picked.length })}
            </Button>
          </Space>
        )
      }
    >
      {result ? (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Alert type="success" showIcon message={t('batch.created', { count: result.created.length })} description={t('batch.next')} />
          {result.skipped.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message={t('batch.skipped')}
              description={result.skipped.map((s) => `${s.address}: ${t(`batch.reasons.${s.reason}`)}`).join('; ')}
            />
          )}
        </Space>
      ) : (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Segmented
            value={kind}
            onChange={setKind}
            options={CALC_KINDS.map((k) => ({ value: k, label: t(`calc.kinds.${k}`) }))}
          />
          <p className="text-sm opacity-70">{t(kind === 'rent' ? 'batch.help' : 'batch.helpOther')}</p>
          <Space wrap align="end">
            <div>
              <div className="text-xs opacity-70">{t('batch.period')}</div>
              <Space>
                <Select
                  style={{ width: 80 }}
                  value={period.month}
                  onChange={(m) => setPeriod((p) => ({ ...p, month: m }))}
                  options={MONTHS.map((m) => ({ value: m, label: m }))}
                />
                <InputNumber
                  style={{ width: 90 }}
                  min={2000}
                  max={2100}
                  value={period.year}
                  onChange={(y) => y && setPeriod((p) => ({ ...p, year: y }))}
                />
              </Space>
            </div>
            {kind !== 'rent' && (
              <div>
                <div className="text-xs opacity-70">{t('calc.fields.currency')}</div>
                <Select style={{ width: 90 }} value={currency} onChange={setCurrency} options={CURRENCY_OPTIONS} />
              </div>
            )}
            {rateCurrencies.length > 0 && (
              <>
                <div>
                  <div className="text-xs opacity-70">{t('calc.fields.rateDate')}</div>
                  <DatePicker format="DD.MM.YYYY" value={rateDate} onChange={setRateDate} />
                </div>
                {rateCurrencies.map((c) => (
                  <div key={c}>
                    <div className="text-xs opacity-70">
                      {t('calc.fields.exchangeRate')} {c}
                    </div>
                    <InputNumber
                      style={{ width: 120 }}
                      min={0}
                      step={0.0001}
                      decimalSeparator=","
                      value={rates[c]}
                      onChange={(v) => setRates((s) => ({ ...s, [c]: v }))}
                    />
                  </div>
                ))}
              </>
            )}
            {kind !== 'rent' && (
              <>
                <div>
                  <div className="text-xs opacity-70">{t('batch.subject')}</div>
                  <Input style={{ width: 260 }} value={subject} onChange={(e) => setSubject(e.target.value)} />
                </div>
                <div>
                  <div className="text-xs opacity-70">{t('fields.project')}</div>
                  <Select
                    allowClear
                    style={{ width: 200 }}
                    value={project}
                    onChange={setProject}
                    options={projects.map((p) => ({ value: p._id, label: `${p.code} ${p.name}` }))}
                  />
                </div>
              </>
            )}
            <div>
              <div className="text-xs opacity-70">{t('calc.fields.payerAccount')}</div>
              <AutoComplete
                allowClear
                style={{ width: 230 }}
                value={payerAccount}
                onChange={setPayerAccount}
                options={payerAccounts}
                placeholder="170-0000000000000-00"
              />
            </div>
            <Input.Search allowClear style={{ width: 200 }} placeholder={t('people.search')} onChange={(e) => setSearch(e.target.value)} />
          </Space>

          {kind === 'rent' ? (
            <Table
              size="small"
              rowKey="_id"
              loading={props.isLoading}
              dataSource={propertyRows}
              columns={propertyColumns}
              pagination={false}
              scroll={{ y: 420 }}
              rowSelection={{
                selectedRowKeys: picked,
                onChange: setPicked,
                getCheckboxProps: (p) => ({ disabled: p.done }),
              }}
            />
          ) : (
            <Table
              size="small"
              rowKey="_id"
              loading={people.isLoading}
              dataSource={otherPeople}
              columns={peopleColumns}
              pagination={false}
              scroll={{ y: 420 }}
              locale={{ emptyText: t('batch.noOtherPeople') }}
              rowSelection={{ selectedRowKeys: picked, onChange: setPicked }}
            />
          )}
          {picked.length > 0 && missingRate && <Alert type="warning" showIcon message={t('errors.calc_invalid_rate')} />}
          {create.isError && <Alert type="error" showIcon message={errorText(t, create.error)} />}
        </Space>
      )}
    </Modal>
  );
}
